import React, { useState } from 'react';

export default function WorkspacePreferences({ profile, onUpdate }) {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    workspaceName: profile?.workspaceName || '',
    emailNotifications: profile?.emailNotifications ?? true,
    systemNotifications: profile?.systemNotifications ?? true
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      await onUpdate(formData);
      setSuccess('Workspace preferences updated successfully');
      setIsEditing(false);
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err?.message || 'Failed to update workspace preferences');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      workspaceName: profile?.workspaceName || '',
      emailNotifications: profile?.emailNotifications ?? true,
      systemNotifications: profile?.systemNotifications ?? true
    });
    setIsEditing(false);
    setError('');
  };

  return (
    <div className="space-y-6">
      <div className="border-b border-gray-200 pb-4">
        <h2 className="text-xl font-semibold text-gray-900">Workspace Preferences</h2>
        <p className="text-sm text-gray-500 mt-1">Customize your workspace settings and notification preferences</p>
      </div>
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <p className="text-sm text-green-600">{success}</p>
        </div>
      )}
      {/* Workspace Settings */}
      <div className="bg-gray-50 rounded-lg p-6 space-y-4">
        <h3 className="text-sm font-medium text-gray-900 mb-4">Workspace Information</h3>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Workspace Name</label>
          <input
            type="text"
            name="workspaceName"
            value={formData?.workspaceName}
            onChange={handleInputChange}
            disabled={!isEditing}
            placeholder="My Workspace"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
          />
          <p className="text-xs text-gray-500 mt-1">This is the name that appears across your workspace</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
          <input
            type="text"
            value={profile?.role}
            disabled
            className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed capitalize"
          />
        </div>
      </div>
      {/* Notification Preferences */}
      <div className="bg-gray-50 rounded-lg p-6">
        <h3 className="text-sm font-medium text-gray-900 mb-4">Notification Preferences</h3>
        <div className="space-y-4">
          <div className="flex items-start">
            <div className="flex items-center h-5">
              <input
                type="checkbox"
                name="emailNotifications"
                checked={formData?.emailNotifications}
                onChange={handleInputChange}
                disabled={!isEditing}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              />
            </div>
            <div className="ml-3">
              <label className="text-sm font-medium text-gray-900">Email Notifications</label>
              <p className="text-xs text-gray-500">Receive email updates about your projects and activity</p>
            </div>
          </div>

          <div className="flex items-start">
            <div className="flex items-center h-5">
              <input
                type="checkbox"
                name="systemNotifications"
                checked={formData?.systemNotifications}
                onChange={handleInputChange}
                disabled={!isEditing}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              />
            </div>
            <div className="ml-3">
              <label className="text-sm font-medium text-gray-900">System Notifications</label>
              <p className="text-xs text-gray-500">Receive in-app notifications about important updates</p>
            </div>
          </div>
        </div>
      </div>
      {/* Default Project Settings */}
      <div className="bg-gray-50 rounded-lg p-6">
        <h3 className="text-sm font-medium text-gray-900 mb-4">Default Project Settings</h3>
        <p className="text-sm text-gray-500">Coming soon: Configure default settings for new projects</p>
      </div>
      {/* Collaboration Preferences */}
      <div className="bg-gray-50 rounded-lg p-6">
        <h3 className="text-sm font-medium text-gray-900 mb-4">Collaboration Preferences</h3>
        <p className="text-sm text-gray-500">Coming soon: Manage team visibility and sharing permissions</p>
      </div>
      {/* Action Buttons */}
      <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
        {isEditing ? (
          <>
            <button
              onClick={handleCancel}
              disabled={saving}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </>
        ) : (
          <button
            onClick={() => setIsEditing(true)}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Edit Preferences
          </button>
        )}
      </div>
    </div>
  );
}